/* eslint-disable max-params, no-param-reassign -- these linting errors come up constantly with reducers */

import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import isNil from "lodash/isNil";

import type { RootState } from "@/store";
import { useAppDispatch } from "@/store/hooks";
import { extraReducersFactory } from "@/store/reducers/reset";
import type { PatientRecommendation } from "@/utils/recommendedGroupClass/modalVisibility.util";

interface AdvancePayload {
  registeredGroupClassExternalId?: string;
}

interface NextQueueState {
  currentIndex: number;
  queue: PatientRecommendation[];
}

/**
 * Given the current queue index and queue, returns the next state after the user
 * dismisses or registers. If registeredGroupClassExternalId is provided, skips
 * any following queue items that recommend that same class.
 */
const getNextStateAfterAdvance = (
  currentIndex: number,
  queue: PatientRecommendation[],
  registeredGroupClassExternalId?: string,
): NextQueueState => {
  let nextIndex = currentIndex + 1;

  if (!isNil(registeredGroupClassExternalId) && nextIndex < queue.length) {
    while (
      nextIndex < queue.length &&
      recommendationMatchesClassId(
        queue[nextIndex],
        registeredGroupClassExternalId,
      )
    ) {
      nextIndex += 1;
    }
  }

  if (nextIndex >= queue.length) {
    return { currentIndex: 0, queue: [] };
  }

  return { currentIndex: nextIndex, queue };
};

const recommendationMatchesClassId = (
  recommendation: PatientRecommendation,
  groupClassExternalId: string,
): boolean => {
  const { instances } = recommendation.recommendedGroupClass;
  return instances.some(
    (inst) => inst.groupClassExternalId === groupClassExternalId,
  );
};

interface RecommendedGroupClassModalSliceState {
  currentIndex: number;
  queue: PatientRecommendation[];
}

const initialState: RecommendedGroupClassModalSliceState = {
  currentIndex: 0,
  queue: [],
};

const recommendedGroupClassModalSlice = createSlice({
  // NOTE: extraReducers is required for resetAllReduxSlices to work as expected
  extraReducers: extraReducersFactory(initialState),
  initialState,
  name: "recommendedGroupClassModal",
  reducers: {
    advance: (state, action: PayloadAction<AdvancePayload | undefined>) => {
      const next = getNextStateAfterAdvance(
        state.currentIndex,
        state.queue,
        action.payload?.registeredGroupClassExternalId,
      );
      state.currentIndex = next.currentIndex;
      state.queue = next.queue;
    },
    resetRecommendedGroupClassModal: (state) => {
      state.queue = [];
      state.currentIndex = 0;
    },
    setQueue: (state, action: PayloadAction<PatientRecommendation[]>) => {
      state.queue = action.payload;
      state.currentIndex = 0;
    },
  },
});

const {
  advance: advanceAction,
  resetRecommendedGroupClassModal: resetRecommendedGroupClassModalAction,
  setQueue: setQueueAction,
} = recommendedGroupClassModalSlice.actions;

const selectCurrentRecommendation = (
  state: RootState,
): PatientRecommendation | undefined =>
  state.recommendedGroupClassModal.queue[
    state.recommendedGroupClassModal.currentIndex
  ];

const selectHasQueue = (state: RootState): boolean => {
  const { currentIndex, queue } = state.recommendedGroupClassModal;
  return currentIndex < queue.length && queue.length > 0;
};

const useRecommendedGroupClassModalDispatch = () => {
  const dispatch = useAppDispatch();

  const advance = (payload?: AdvancePayload) =>
    dispatch(advanceAction(payload));
  const resetRecommendedGroupClassModal = () =>
    dispatch(resetRecommendedGroupClassModalAction());
  const setQueue = (queue: PatientRecommendation[]) =>
    dispatch(setQueueAction(queue));

  return {
    advance,
    resetRecommendedGroupClassModal,
    setQueue,
  };
};

export {
  advanceAction,
  getNextStateAfterAdvance,
  resetRecommendedGroupClassModalAction,
  selectCurrentRecommendation,
  selectHasQueue,
  setQueueAction,
  useRecommendedGroupClassModalDispatch,
};
export type { NextQueueState };
export default recommendedGroupClassModalSlice.reducer;
